import dayjs from "dayjs";
import { CalendarEvent } from "../models/calendarEvent";
import { CalendarProvider } from "../models/user";
import { msGraphAuthService } from "../services/MsGraphAuthService";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OUTLOOK_PROXY_EMAIL_PATTERN = /^outlook[-_][a-z0-9]+@outlook\.com$/i;

export const normalizeEmail = (value?: string): string =>
  (value ?? "").trim().toLowerCase();

const hasExplicitOffset = (value: string): boolean =>
  /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value);

const isEmailLike = (value?: string): boolean =>
  EMAIL_PATTERN.test((value ?? "").trim());

const isOutlookProxyEmail = (value?: string): boolean =>
  OUTLOOK_PROXY_EMAIL_PATTERN.test((value ?? "").trim());

export const parseDateOnlyLocal = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

export const parseFloatingDateTime = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;

  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(
    normalized,
  );
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6] || "0");
    return new Date(year, month - 1, day, hour, minute, second, 0);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const getOffsetMinutesForTimeZone = (
  utcTimestamp: number,
  timeZone: string,
): number => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date(utcTimestamp));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value || "0");

  const asUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour"),
    read("minute"),
    read("second"),
  );

  return (asUtc - utcTimestamp) / 60000;
};

export const parseDateTimeWithZone = (
  dateTime?: string,
  timeZone?: string,
): Date | undefined => {
  if (!dateTime) return undefined;
  const normalizedDateTime = dateTime.trim();
  if (!normalizedDateTime) return undefined;

  if (hasExplicitOffset(normalizedDateTime)) {
    const parsed = new Date(normalizedDateTime);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const normalizedZone = (timeZone || "").trim();
  if (!normalizedZone) {
    const parsed = new Date(normalizedDateTime);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  if (/^(utc|gmt|etc\/utc)$/i.test(normalizedZone)) {
    const parsed = new Date(`${normalizedDateTime}Z`);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(
    normalizedDateTime,
  );
  if (!match) {
    const parsed = new Date(normalizedDateTime);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || "0");
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  try {
    const initialOffset = getOffsetMinutesForTimeZone(naiveUtc, normalizedZone);
    let correctedUtc = naiveUtc - initialOffset * 60000;
    const refinedOffset = getOffsetMinutesForTimeZone(
      correctedUtc,
      normalizedZone,
    );
    if (refinedOffset !== initialOffset) {
      correctedUtc = naiveUtc - refinedOffset * 60000;
    }
    return new Date(correctedUtc);
  } catch {
    const parsed = new Date(normalizedDateTime);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
};

export const resolveCalendarOrganizer = ({
  address,
  name,
  accountUsername,
}: {
  address?: string;
  name?: string;
  accountUsername?: string;
}): string => {
  const normalizedAddress = (address ?? "").trim();
  const normalizedName = (name ?? "").trim();
  const normalizedAccountUsername = (accountUsername ?? "").trim();

  if (normalizedAddress && !isOutlookProxyEmail(normalizedAddress)) {
    return normalizedAddress;
  }

  if (isEmailLike(normalizedName)) {
    return normalizedName;
  }

  if (isEmailLike(normalizedAccountUsername)) {
    return normalizedAccountUsername;
  }

  if (normalizedName) {
    return normalizedName;
  }

  if (normalizedAddress) {
    return normalizedAddress;
  }

  return normalizedAccountUsername;
};

// ─────────────────────────────────────────────────────────────────────
// Shared calendar helpers used by the Meetings and Coaching Navigator
// event lists. See .github/instructions/calendar-events.instructions.md.
// ─────────────────────────────────────────────────────────────────────

export type DateRange = {
  startDateTime: Date;
  endDateTime: Date;
};

export const buildWindowDateRange = (
  pastDays: number,
  futureDays: number,
): DateRange => {
  const today = dayjs().startOf("day");
  return {
    startDateTime: today.subtract(pastDays, "day").toDate(),
    endDateTime: today.add(futureDays + 1, "day").toDate(),
  };
};

export const mergeDateRanges = (
  current: DateRange | null,
  incoming: DateRange,
): DateRange => {
  if (!current) return incoming;
  return {
    startDateTime:
      incoming.startDateTime.getTime() < current.startDateTime.getTime()
        ? incoming.startDateTime
        : current.startDateTime,
    endDateTime:
      incoming.endDateTime.getTime() > current.endDateTime.getTime()
        ? incoming.endDateTime
        : current.endDateTime,
  };
};

export const getEventUniqueKey = (event: CalendarEvent): string => {
  const account = normalizeEmail(event.accountUsername);
  const provider = (event.provider ?? "unknown").toLowerCase();
  if (event.id) {
    return `${provider}:${account}:${event.id}`;
  }
  return `${provider}:${account}:${event.name}:${new Date(event.startDate).toISOString()}`;
};

export const getOutlookAccountsInMsalCache = (): Set<string> => {
  try {
    return new Set(
      msGraphAuthService
        .getAllAccounts()
        .map((account) => normalizeEmail(account.username))
        .filter((username) => !!username),
    );
  } catch {
    return new Set<string>();
  }
};

interface MapEventOptions {
  /** Prefix id with `${provider}:${account}:` (needed when the same event id
   * can appear across multiple connected accounts and must not collide). */
  namespaceId?: boolean;
  /** Populate `location` from provider payload. */
  includeLocation?: boolean;
  /** Populate `onlineMeetingUrl` / `isOnlineMeeting` (Outlook). */
  includeOnlineMeeting?: boolean;
  /** Fallback name when the provider omits a subject/summary. */
  defaultName?: string;
}

const namespaceEventId = (
  provider: CalendarProvider,
  accountUsername: string | undefined,
  rawId: string | undefined,
): string | undefined => {
  if (!rawId) return undefined;
  return `${provider}:${accountUsername || "unknown"}:${rawId}`;
};

export const mapOutlookEvent = (
  e: any,
  accountUsername?: string,
  options: MapEventOptions = {},
): CalendarEvent => {
  const mapped: CalendarEvent = {
    id: options.namespaceId
      ? namespaceEventId(CalendarProvider.Outlook, accountUsername, e.id)
      : e.id,
    uid: e.uid,
    name: e.subject || options.defaultName || "(No title)",
    startDate:
      parseDateTimeWithZone(e.start?.dateTime, e.start?.timeZone) ||
      parseFloatingDateTime(e.start?.dateTime) ||
      new Date(),
    endDate:
      parseDateTimeWithZone(e.end?.dateTime, e.end?.timeZone) ||
      parseFloatingDateTime(e.end?.dateTime),
    creator: resolveCalendarOrganizer({
      address: e.organizer?.emailAddress?.address,
      name: e.organizer?.emailAddress?.name,
      accountUsername,
    }),
    invitees:
      e.attendees?.map((a: any) => a?.emailAddress?.address).filter(Boolean) ??
      [],
    provider: CalendarProvider.Outlook,
    accountUsername,
  };

  const outlookDisplayName = (e.organizer?.emailAddress?.name ?? "").trim();
  if (outlookDisplayName && !isEmailLike(outlookDisplayName)) {
    mapped.creatorName = outlookDisplayName;
  }

  if (options.includeLocation) {
    mapped.location = e.location?.displayName || "";
  }
  if (options.includeOnlineMeeting) {
    mapped.onlineMeetingUrl = e.onlineMeeting?.joinUrl || e.onlineMeetingUrl;
    mapped.isOnlineMeeting = e.isOnlineMeeting;
  }

  return mapped;
};

export const mapGoogleEvent = (
  e: any,
  accountUsername?: string,
  options: MapEventOptions = {},
): CalendarEvent => {
  const mapped: CalendarEvent = {
    id: options.namespaceId
      ? namespaceEventId(CalendarProvider.Google, accountUsername, e.id)
      : e.id,
    uid: e.iCalUID,
    name: e.summary || options.defaultName || "(No title)",
    startDate:
      parseDateTimeWithZone(e.start?.dateTime, e.start?.timeZone) ||
      parseDateOnlyLocal(e.start?.date) ||
      new Date(),
    endDate:
      parseDateTimeWithZone(e.end?.dateTime, e.end?.timeZone) ||
      parseDateOnlyLocal(e.end?.date),
    creator: resolveCalendarOrganizer({
      address: e.organizer?.email,
      accountUsername,
    }),
    invitees: e.attendees?.map((a: any) => a?.email).filter(Boolean) ?? [],
    provider: CalendarProvider.Google,
    accountUsername,
  };

  // Prefer organizer.displayName; fall back to creator.displayName.
  // The Google API omits displayName for personal Gmail events, so both
  // fields are checked before giving up and showing the raw email address.
  const googleDisplayName = (
    e.organizer?.displayName ||
    e.creator?.displayName ||
    ""
  ).trim();
  if (googleDisplayName && !isEmailLike(googleDisplayName)) {
    mapped.creatorName = googleDisplayName;
  }

  if (options.includeLocation) {
    mapped.location = e.location || "";
  }

  return mapped;
};

/**
 * Per-connection calendar status shown in the UI:
 * - `active`      — green ✓, fetched successfully in the last refresh.
 * - `unavailable` — red ✗, Outlook account is not in this browser's MSAL cache
 *                   (user signed in on another device). Needs reconnect here.
 * - `revoked`     — red ✗, Google auth expired / was revoked by the user.
 *                   Auto-disconnected on the server; needs a fresh connect.
 * - `failed`      — amber !, transient fetch error (network / 5xx / rate-limit).
 */
export type CalendarConnectionStatus =
  | "active"
  | "unavailable"
  | "revoked"
  | "failed";

export type CalendarConnectionStatusMap = Record<
  string,
  CalendarConnectionStatus
>;

/** Stable key for the status map, matching the shape used by
 * `getEventUniqueKey` (provider + normalized account username). */
export const connectionStatusKey = (
  provider: string | undefined,
  accountUsername: string | undefined,
): string =>
  `${(provider ?? "unknown").toLowerCase()}:${normalizeEmail(accountUsername)}`;

/** Classification of a failed Google fetch — used to pick between
 * "token revoked → auto-disconnect" and "transient failure → just warn". */
export const isGoogleTokenRevokedError = (reason: unknown): boolean => {
  const axiosError = reason as {
    response?: { status?: number; data?: { code?: string } };
  };
  return (
    axiosError?.response?.status === 401 ||
    axiosError?.response?.data?.code === "GOOGLE_TOKEN_REVOKED"
  );
};
